import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, setToken, apiErr } from "@/lib/api";
import { Logo, Button, Field, Input, Select } from "@/lib/ui";
import { useAuth } from "@/context/Auth";

const PROOFS = ["Aadhar Card", "Driving License", "Election Card", "Ration Card"];
const EMPTY = { name: "", phone: "", email: "", address: "", pan: "", proof_type: "", proof_image: "", password: "" };

export default function ReferorAuth() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState(EMPTY);
  const [login, setLogin] = useState({ phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [proofPreview, setProofPreview] = useState(null);
  const fileRef = useRef();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadProof = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error(`Image is ${(file.size / 1048576).toFixed(1)} MB — maximum size is 8 MB`); return; }
    setUploading(true); setProgress(0);
    toast.info("Uploading ID proof…");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/public/referor-proof", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
        onUploadProgress: (ev) => ev.total && setProgress(Math.round((ev.loaded * 100) / ev.total)) });
      set("proof_image", data.path); setProofPreview(URL.createObjectURL(file)); toast.success("ID proof uploaded");
    } catch (e) { toast.error(apiErr(e) || "Upload failed. Check your connection and try again."); set("proof_image", ""); setProofPreview(null); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "register") {
        if (!form.proof_image) { toast.error("ID proof image is mandatory"); return; }
        const { data } = await api.post("/auth/referor/register", form);
        toast.success(data.message); setForm(EMPTY); setMode("login");
      } else {
        const { data } = await api.post("/auth/referor/login", login);
        setToken(data.token, "referor"); setUser(data.user); nav("/referor/app");
      }
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4 py-10">
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-white p-8 fade-up">
        <Logo />
        <h1 className="mt-4 font-head text-2xl font-extrabold text-slate-900"><i className="fa-solid fa-user-tie mr-2 text-orange-600" />Referor Portal</h1>
        <div className="mt-5 flex rounded-lg bg-slate-100 p-1 text-sm font-semibold">
          <button data-testid="referor-tab-login" onClick={() => setMode("login")} className={`flex-1 rounded-md py-2 ${mode === "login" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Login</button>
          <button data-testid="referor-tab-register" onClick={() => setMode("register")} className={`flex-1 rounded-md py-2 ${mode === "register" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Register as Referor</button>
        </div>
        <form onSubmit={submit} className="mt-5 space-y-3">
          {mode === "login" ? (
            <>
              <Field label="Mobile Number" required><Input data-testid="referor-login-phone" value={login.phone} onChange={(e) => setLogin({ ...login, phone: e.target.value })} placeholder="10-digit mobile" required /></Field>
              <Field label="Password" required><Input data-testid="referor-login-password" type="password" value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} required /></Field>
            </>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Full Name" required><Input data-testid="referor-name" value={form.name} onChange={(e) => set("name", e.target.value)} required /></Field>
                <Field label="Mobile Number" required><Input data-testid="referor-phone" value={form.phone} onChange={(e) => set("phone", e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10-digit mobile" required /></Field>
                <Field label="Email Id" required><Input data-testid="referor-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required /></Field>
                <Field label="PAN No." required><Input data-testid="referor-pan" value={form.pan} onChange={(e) => set("pan", e.target.value.toUpperCase().slice(0, 10))} placeholder="ABCDE1234F" required /></Field>
              </div>
              <Field label="Address" required><textarea data-testid="referor-address" value={form.address} onChange={(e) => set("address", e.target.value)} rows={2} required className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" /></Field>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Valid ID Proof" required>
                  <Select data-testid="referor-proof-type" value={form.proof_type} onChange={(e) => set("proof_type", e.target.value)} required>
                    <option value="">Select proof</option>{PROOFS.map((p) => <option key={p}>{p}</option>)}
                  </Select>
                </Field>
                <Field label="Proof Image" required hint="JPG, PNG, WEBP or HEIC · max 8 MB">
                  <label data-testid="referor-proof-upload" className={`relative flex h-[42px] cursor-pointer items-center gap-2 rounded-lg border border-dashed px-3 text-xs ${form.proof_image ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-slate-300 text-slate-500 hover:border-orange-400"}`}>
                    <i className={`fa-solid ${uploading ? "fa-spinner fa-spin" : form.proof_image ? "fa-circle-check" : "fa-upload"}`} /> {uploading ? `Uploading… ${progress}%` : form.proof_image ? "Uploaded · tap to change" : "Tap to upload photo (max 8 MB)"}
                    <input ref={fileRef} type="file" accept="image/*,.heic,.heif" data-testid="referor-proof-input" className="absolute inset-0 h-full w-full cursor-pointer opacity-0" onChange={(e) => uploadProof(e.target.files && e.target.files[0])} />
                  </label>
                </Field>
              </div>
              {proofPreview && <img src={proofPreview} alt="proof" className="h-24 rounded-lg border object-cover" />}
              <Field label="Create Password" required hint="Min 6 characters"><Input data-testid="referor-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={6} /></Field>
            </>
          )}
          <Button data-testid="referor-submit-btn" type="submit" disabled={loading || uploading} className="w-full">{loading ? "Please wait…" : mode === "register" ? "Submit for Approval" : "Login"}</Button>
        </form>
        <button onClick={() => nav("/seller")} className="mt-4 text-xs font-semibold text-slate-400 hover:text-slate-600"><i className="fa-solid fa-chevron-left mr-1" /> Back</button>
      </div>
    </div>
  );
}
